// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';
import { Cell, Row } from 'react-table';

import { IconCaret2 } from '../..';

interface ExpandingRowProps<D extends object> {
    /**
     * Width for the caret icon
     * @defaultValue 16
     */
    caretIconWidth?: number;
    /**
     * Row height
     */
    height: number;
    /**
     * Tailwind size to apply to text for grouped rows
     * @defaultValue 'sm'
     */
    groupTextSize?: 'xs' | 'sm' | 'base' | 'lg';
    /**
     * `index` parameter provided by react-window's render props
     */
    index: number;
    /**
     * The react-table row instance
     */
    row: Row<D>;
    /**
     * The `prepareRow()` function
     */
    prepareRow(row: Row<D>): void;
    /**
     * Tailwind size to apply to text
     * @defaultValue 'xs'
     */
    textSize?: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders a table row with a caret indicating whether it is expanded or not. The react-table
 * `useTable` hook must be passed the `useExpanded` and `useGroupBy` hooks. Not intended for use
 * within a render function for <FixedSizeList> from react-window.
 * @param props - {@link ExpandingRowProps}
 */
export function ExpandingRow<D extends object>({
    caretIconWidth = 10,
    height,
    groupTextSize = 'sm',
    index,
    row,
    prepareRow,
    textSize = 'xs',
}: ExpandingRowProps<D>): JSX.Element {
    prepareRow(row);

    const ariaRowIndex = index + 1;
    const evenRow = ariaRowIndex % 2 === 0;
    const { key: rowKey, ...rowRest } = row.getRowProps();
    const classNames = cx('tr relative flex items-center', {
        'even-row': !row.isGrouped && evenRow,
        group: row.isGrouped && index !== 0,
        'group-expanded': row.isGrouped && row.isExpanded,
    });

    return (
        <div
            key={rowKey}
            {...rowRest}
            aria-rowindex={ariaRowIndex}
            className={classNames}
            style={{
                height,
            }}
        >
            {row.cells.map((cell: Cell<D>) => {
                const { key: cellKey, ...cellRest } = cell.getCellProps();
                const { isAggregated, isGrouped, isPlaceholder } = cell;
                const showGrouped = isGrouped;
                const showAggregated = !isGrouped && isAggregated;
                const showCell = !isGrouped && !isAggregated && !isPlaceholder;

                return (
                    <div
                        key={cellKey}
                        {...cellRest}
                        className={cx(`td text-${textSize}`, {
                            'font-medium': !showCell,
                        })}
                    >
                        {showGrouped && (
                            <div
                                className={`flex items-center text-${groupTextSize}`}
                            >
                                <span {...row.getToggleRowExpandedProps()}>
                                    <IconCaret2
                                        addClasses="mr-1 mb-1"
                                        width={caretIconWidth}
                                        rotation={row.isExpanded ? 0 : 270}
                                    />
                                </span>{' '}
                                {cell.render('Cell')}
                            </div>
                        )}
                        {showAggregated && <>{cell.render('Aggregated')}</>}
                        {showCell && <>{cell.render('Cell')}</>}
                    </div>
                );
            })}
        </div>
    );
}
